/**
 * Self-Healing Router — middleware for building robust source citation URLs.
 *
 * Handles edge cases where source data is incomplete or malformed:
 * - Missing document_id → log warning, return null
 * - Missing ref_ids → fall back to document root (page 1)
 * - Invalid page numbers → omit page param
 *
 * Also provides logging for broken link analytics.
 */

interface SourceData {
  document_id?: string;
  ref_ids?: number[];
  metadata?: {
    ref_id?: number | string;
    all_refs?: number[];
    source_block_refs?: number[];
    block_id?: string;
    page?: number;
    title?: string;
    is_transcript?: boolean;
    start_time?: number;
    [key: string]: any;
  };
  is_transcript?: boolean;
  start_time?: number;
  [key: string]: any;
}

interface ResolvedUrl {
  url: string;
  fallback: boolean;
  reason?: string;
}

/**
 * Resolve a source into a valid citation URL.
 *
 * Returns null if the source has no document_id (unrecoverable).
 * Sets fallback=true if we had to degrade (e.g., missing ref_ids).
 */
export function resolveSourceUrl(source: SourceData): ResolvedUrl | null {
  const docId = source.document_id;

  if (!docId) {
    logBrokenLink('missing_document_id', source);
    return null;
  }

  // Transcript path
  const isTranscript = source.is_transcript || source.metadata?.is_transcript;
  if (isTranscript) {
    return resolveTranscriptUrl(docId, source);
  }

  // PDF / document path
  return resolveDocumentUrl(docId, source);
}

function resolveDocumentUrl(docId: string, source: SourceData): ResolvedUrl {
  const params = new URLSearchParams();
  params.set('id', docId);

  // Priority 1: source_block_refs (all blocks in chunk)
  const sourceBlockRefs = source.ref_ids
    || source.metadata?.all_refs
    || source.metadata?.source_block_refs
    || [];

  if (sourceBlockRefs.length > 0) {
    params.set('highlightRefs', sourceBlockRefs.join(','));
    return { url: `/document-pro?${params.toString()}`, fallback: false };
  }

  // Priority 2: single ref_id
  const refId = source.metadata?.ref_id;
  if (refId != null) {
    params.set('highlightRef', String(refId));
    const page = source.metadata?.page;
    if (page != null && Number.isFinite(Number(page)) && Number(page) > 0) {
      params.set('page', String(page));
    }
    return { url: `/document-pro?${params.toString()}`, fallback: false };
  }

  // Priority 3: block_id
  const blockId = source.metadata?.block_id;
  if (blockId) {
    params.set('highlightBlock', blockId);
    return { url: `/document-pro?${params.toString()}`, fallback: false };
  }

  // Fallback: open document at page 1 (no highlight)
  logBrokenLink('missing_ref_data', source);
  return {
    url: `/document-pro?${params.toString()}`,
    fallback: true,
    reason: 'No ref_id, ref_ids, or block_id — opening document root',
  };
}

function resolveTranscriptUrl(docId: string, source: SourceData): ResolvedUrl {
  const params = new URLSearchParams();
  params.set('docId', docId);

  const startTime = source.start_time ?? source.metadata?.start_time;
  if (startTime != null && Number.isFinite(Number(startTime))) {
    params.set('t', String(Math.floor(Number(startTime))));
  }

  const refId = source.metadata?.ref_id;
  const fragment = refId != null ? `#para-${refId}` : '';

  return {
    url: `/replay-center?${params.toString()}${fragment}`,
    fallback: false,
  };
}

/**
 * Log a broken link event for analytics / debugging.
 * Fire-and-forget — never throws.
 */
function logBrokenLink(reason: string, source: SourceData): void {
  try {
    const payload = {
      reason,
      document_id: source.document_id || null,
      ref_id: source.metadata?.ref_id || null,
      title: source.metadata?.title || null,
      timestamp: new Date().toISOString(),
    };

    console.warn('[SELF-HEALING] Broken link detected:', payload);

    // Fire-and-forget POST to analytics endpoint (if backend supports it)
    const apiBase = process.env.REACT_APP_API_URL || '';
    if (apiBase) {
      fetch(`${apiBase}/v3/analytics/broken-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {
        // Silently ignore — analytics is best-effort
      });
    }
  } catch {
    // Never throw from logging
  }
}
